import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { stagePaymentsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  Plus, Loader2, Trash2, CheckCircle, ExternalLink, Layers, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGES = {
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  invoiced: { label: 'Invoiced', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-50 text-rose-600 border-rose-200' },
};

const safeNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Stage Payments — interim invoices between deposit and final bill.
 *
 * Sum constraint (per admin choice 2026-05-15):
 *     deposit + finance + final + Σ active stages = system_total_cost
 * The component shows a live warning banner when the totals don't match.
 *
 * Cancelled stages are excluded from the sum (so admins can park retired
 * stages without breaking the balance).
 */
export const StagePaymentsSection = ({ customer, isAdmin = false, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ label: '', amount: '', due_date: '', notes: '', raise_xero_invoice: false });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // current row id being updated

  const fetchAll = async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const r = await stagePaymentsAPI.list(customer.id);
      setItems(r.data || []);
    } catch (e) {
      toast.error('Could not load stage payments');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [customer?.id]);

  const stagesSum = items
    .filter(i => i.status !== 'cancelled')
    .reduce((acc, i) => acc + safeNum(i.amount), 0);

  const deposit = safeNum(customer?.deposit_amount);
  const finance = customer?.on_finance ? safeNum(customer?.finance_amount) : 0;
  const finalBill = safeNum(customer?.final_bill_amount);
  const total = safeNum(customer?.system_total_cost) * (1 + safeNum(customer?.vat_rate) / 100);
  const breakdownSum = deposit + finance + finalBill + stagesSum;
  const diff = total - breakdownSum;
  const balanced = total > 0 ? Math.abs(diff) < 0.01 : false;

  const resetDraft = () => setDraft({ label: '', amount: '', due_date: '', notes: '', raise_xero_invoice: false });

  const create = async () => {
    if (!draft.label.trim() || !draft.amount) {
      toast.error('Label and amount are required');
      return;
    }
    setSaving(true);
    try {
      const r = await stagePaymentsAPI.create(customer.id, {
        ...draft,
        amount: parseFloat(draft.amount),
      });
      setItems(prev => [...prev, r.data]);
      toast.success(r.data.xero_invoice_id ? `Stage payment + Xero invoice ${r.data.xero_invoice_number || ''} created` : 'Stage payment added');
      setCreateOpen(false);
      resetDraft();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create stage payment');
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (sp) => {
    setBusy(sp.id);
    try {
      const r = await stagePaymentsAPI.update(customer.id, sp.id, { status: 'paid' });
      setItems(prev => prev.map(x => x.id === sp.id ? r.data : x));
      toast.success('Marked as paid');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error('Could not mark paid');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (sp) => {
    if (!window.confirm(`Delete stage payment "${sp.label}"?`)) return;
    setBusy(sp.id);
    try {
      await stagePaymentsAPI.remove(customer.id, sp.id);
      setItems(prev => prev.filter(x => x.id !== sp.id));
      toast.success('Stage payment deleted');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="stage-payments-section">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            Stage Payments
          </CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            Interim invoices between deposit and final bill. Sum should equal{' '}
            <strong>System Total − Deposit − Finance − Final</strong>.
          </p>
        </div>
        {isAdmin && (
          <Button
            size="sm"
            onClick={() => { resetDraft(); setCreateOpen(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="stage-payment-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Stage
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Balance check */}
        {total > 0 && (
          <div
            className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${
              balanced
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
            data-testid="stage-payments-balance"
          >
            {balanced ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />}
            <div className="flex-1">
              <p className="font-semibold">
                {balanced ? 'Payments balanced.' : `Out of balance by ${formatCurrency(Math.abs(diff))}`}
              </p>
              <p className="mt-0.5">
                {formatCurrency(total)} total (inc VAT) ={' '}
                <span className="font-mono">
                  {formatCurrency(deposit)} deposit + {formatCurrency(finance)} finance + {formatCurrency(finalBill)} final + {formatCurrency(stagesSum)} stages
                </span>
                {!balanced && diff > 0 && (
                  <span className="block mt-1">£{Math.abs(diff).toFixed(2)} unallocated.</span>
                )}
                {!balanced && diff < 0 && (
                  <span className="block mt-1">£{Math.abs(diff).toFixed(2)} over total.</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            No stage payments yet.
            {isAdmin && (
              <>
                {' '}Click <strong>Add Stage</strong> to create one.
              </>
            )}
          </p>
        ) : (
          <div className="space-y-2">
            {items.map(sp => {
              const sb = STATUS_BADGES[sp.status] || STATUS_BADGES.pending;
              return (
                <div
                  key={sp.id}
                  className="p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`stage-payment-${sp.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{sp.label}</p>
                        <Badge className={`${sb.cls} border text-[10px]`}>{sb.label}</Badge>
                        {sp.xero_invoice_number && (
                          <a
                            href={sp.xero_invoice_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-blue-600 hover:text-blue-700 inline-flex items-center"
                            data-testid={`stage-payment-xero-${sp.id}`}
                          >
                            Xero {sp.xero_invoice_number} <ExternalLink className="w-3 h-3 ml-0.5" />
                          </a>
                        )}
                      </div>
                      {sp.notes && <p className="text-xs text-slate-500 mt-1">{sp.notes}</p>}
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                        {sp.due_date && <span>Due {formatDate(sp.due_date)}</span>}
                        {sp.paid_date && <span className="text-emerald-600">Paid {formatDate(sp.paid_date)}</span>}
                        {sp.created_by && <span>· by {sp.created_by}</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(safeNum(sp.amount))}</p>
                      {isAdmin && (
                        <div className="flex gap-1 mt-1 justify-end">
                          {sp.status !== 'paid' && sp.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px] text-emerald-700"
                              disabled={busy === sp.id}
                              onClick={() => markPaid(sp)}
                              data-testid={`stage-payment-pay-${sp.id}`}
                            >
                              {busy === sp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark Paid'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-rose-500 hover:text-rose-700"
                            disabled={busy === sp.id}
                            onClick={() => remove(sp)}
                            data-testid={`stage-payment-delete-${sp.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="stage-payment-create-dialog">
          <DialogHeader>
            <DialogTitle>New Stage Payment</DialogTitle>
            <DialogDescription>Interim invoice between deposit and final bill.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label *</Label>
              <Input
                value={draft.label}
                onChange={e => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Stage 1: Materials delivered"
                data-testid="stage-payment-label"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.amount}
                  onChange={e => setDraft({ ...draft, amount: e.target.value })}
                  data-testid="stage-payment-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input
                  type="date"
                  value={draft.due_date}
                  onChange={e => setDraft({ ...draft, due_date: e.target.value })}
                  data-testid="stage-payment-due"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={draft.notes}
                onChange={e => setDraft({ ...draft, notes: e.target.value })}
                rows={2}
                placeholder="Optional context for the customer / Xero description"
                data-testid="stage-payment-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.raise_xero_invoice}
                onCheckedChange={(v) => setDraft({ ...draft, raise_xero_invoice: !!v })}
                data-testid="stage-payment-raise-xero"
              />
              <span>Raise Xero invoice now (also shown on Customer Portal)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={create}
              disabled={saving || !draft.label.trim() || !draft.amount}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="stage-payment-create"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StagePaymentsSection;
